import { useState } from "react";
import { Cookie, X } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useLanguage, pick } from "@/lib/i18n";
import { COOKIE } from "@/lib/translations";

const CONSENT_KEY = "dino-cookie-consent";

export default function CookieBanner() {
    const { lang } = useLanguage();
    const t = pick(COOKIE, lang);
    const [visible, setVisible] = useState(() => {
        try {
            return !window.localStorage.getItem(CONSENT_KEY);
        } catch (_e) {
            return true;
        }
    });
    const [showPrefs, setShowPrefs] = useState(false);
    const [nonEssential, setNonEssential] = useState(false);

    const persist = (choice) => {
        try {
            window.localStorage.setItem(
                CONSENT_KEY,
                JSON.stringify({ ...choice, ts: new Date().toISOString() })
            );
        } catch (_e) {
            // ignore
        }
        setVisible(false);
        setShowPrefs(false);
    };

    if (!visible) return null;

    return (
        <>
            <div
                role="dialog"
                aria-live="polite"
                data-testid="cookie-banner"
                className="fixed bottom-0 inset-x-0 z-50 px-4 sm:px-6 pb-4 sm:pb-6 animate-fade-up"
            >
                <div className="max-w-5xl mx-auto rounded-3xl bg-jungle-900 text-cream-50 shadow-card border-2 border-amber-500 p-5 sm:p-6 grid md:grid-cols-[auto_1fr_auto] gap-4 md:gap-6 items-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-500 text-jungle-900 flex-shrink-0">
                        <Cookie className="w-6 h-6" strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-display text-lg font-bold mb-1">{t.title}</h3>
                        <p className="text-sm text-cream-50/80 leading-relaxed">{t.body}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-2 flex-shrink-0">
                        <Button
                            type="button"
                            data-testid="cookie-manage"
                            variant="ghost"
                            onClick={() => setShowPrefs(true)}
                            className="rounded-full text-cream-50 hover:bg-cream-50/10 hover:text-amber-400 font-bold"
                        >
                            {t.manage}
                        </Button>
                        <Button
                            type="button"
                            data-testid="cookie-reject-all"
                            variant="outline"
                            onClick={() => persist({ essential: true, security: true, nonEssential: false })}
                            className="rounded-full border-2 border-cream-50/40 bg-transparent text-cream-50 hover:bg-cream-50/10 hover:text-cream-50 font-bold"
                        >
                            {t.reject}
                        </Button>
                        <Button
                            type="button"
                            data-testid="cookie-accept-all"
                            onClick={() => persist({ essential: true, security: true, nonEssential: true })}
                            className="rounded-full bg-amber-500 text-jungle-900 hover:bg-amber-400 font-bold shadow-playful-amber-sm hover:shadow-none hover:translate-y-[2px] transition-all"
                        >
                            {t.accept}
                        </Button>
                    </div>
                </div>
            </div>

            <Dialog open={showPrefs} onOpenChange={setShowPrefs}>
                <DialogContent className="rounded-3xl border-2 border-jungle-700/15 max-w-lg" data-testid="cookie-prefs-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-jungle-900">{t.prefsTitle}</DialogTitle>
                        <DialogDescription className="text-ink-500">
                            {t.prefsBody}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <PrefRow title={t.essential} desc={t.essentialDesc} forcedOn />
                        <PrefRow title={t.security} desc={t.securityDesc} forcedOn />
                        <PrefRow
                            title={t.nonEssential}
                            desc={t.nonEssentialDesc}
                            value={nonEssential}
                            onChange={setNonEssential}
                            testId="cookie-pref-non-essential"
                        />
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setShowPrefs(false)}
                            className="rounded-full"
                        >
                            <X className="w-4 h-4 mr-1" /> Close
                        </Button>
                        <Button
                            type="button"
                            data-testid="cookie-save-prefs"
                            onClick={() => persist({ essential: true, security: true, nonEssential })}
                            className="rounded-full bg-jungle-700 text-cream-50 hover:bg-jungle-900"
                        >
                            {t.save}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function PrefRow({ title, desc, value, onChange, forcedOn, testId }) {
    return (
        <div className="flex items-start justify-between gap-4 p-4 rounded-2xl bg-cream-100 border border-jungle-700/10">
            <div className="min-w-0">
                <div className="font-display font-semibold text-jungle-900">{title}</div>
                <p className="text-sm text-ink-500 mt-0.5">{desc}</p>
            </div>
            <Switch
                checked={forcedOn ? true : value}
                onCheckedChange={onChange}
                disabled={forcedOn}
                data-testid={testId}
                className="data-[state=checked]:bg-jungle-700"
            />
        </div>
    );
}
